<template>
  <base-event-log-item
    :icon="icon"
    :icon-color="iconColor"
    :researcher="referenceEventLog.eventBy"
    :date="referenceEventLog.createdAt"
    :bottom-node="bottomNode"
    :color="color"
  >
    <div class="text-overflow-ellipsis">
      <span class="font-weight-medium">
        {{ target }}
      </span>
      <span class="font-weight-light">
        {{ verbs }}
      </span>
      <span class="font-weight-medium">
        {{ outcome }}
      </span>
    </div>
    <!-- The slot renders in a flex row, so inline detail text squeezes the
         event title; a hover tooltip on a small info icon avoids that. -->
    <!-- `top`: event-log entries stack against the bottom of the panel, so a
         bottom-placed tooltip clips at the viewport edge. -->
    <v-tooltip
      v-if="details"
      top
      max-width="360"
      data-testid="event-log-vote-details"
    >
      <template #activator="{ on, attrs }">
        <v-icon
          size="14"
          class="ml-1 material-icons-outlined"
          color="grey darken-1"
          data-testid="event-log-vote-details-icon"
          v-bind="attrs"
          v-on="on"
        >
          info
        </v-icon>
      </template>
      <div>
        <div v-if="details.criterion">
          Criterion: {{ details.criterion }}
        </div>
        <div v-if="details.notes">Notes: {{ details.notes }}</div>
      </div>
    </v-tooltip>
  </base-event-log-item>
</template>

<script>
import BaseEventLogItem from './BaseEventLogItem';
import { ReferenceEventLog, Reference, Researcher } from '@/models';
import { ReferenceEvent, InclusionAssessment } from '@/enums';
import { startCase } from 'lodash';

const getIcon = (outcome) => {
  switch (outcome) {
    case InclusionAssessment.INCLUDE:
      return { name: 'done', color: 'success' };
    case InclusionAssessment.EXCLUDE:
      return { name: 'close', color: 'error' };
    default:
      throw new Error('Invalid inclusion assessment');
  }
};

// Vote content must stay hidden if EITHER the client's own blind guard
// (shouldBeBlind) says so, OR the server flags the vote as blinded.
// Outcome events carry no `fullTextReviewVote` payload, so this helper is
// only ever used by the vote style props below.
const isVoteBlind = ({ payload }, shouldBeBlind) =>
  shouldBeBlind || payload.fullTextReviewVote.blinded;

const FULL_TEXT_REVIEW_VOTE_STYLE_PROPS = {
  target: 'Full Text Review Vote',
  verbs: (eventLog) =>
    eventLog.event === ReferenceEvent.FULL_TEXT_REVIEW_VOTE_CREATE
      ? 'set to'
      : 'changed to',
  icon: (eventLog, shouldBeBlind) =>
    isVoteBlind(eventLog, shouldBeBlind)
      ? { name: 'help', color: 'grey darken-2' }
      : getIcon(eventLog.payload.fullTextReviewVote.vote),
  outcome: (eventLog, shouldBeBlind) => {
    return isVoteBlind(eventLog, shouldBeBlind)
      ? '-'
      : startCase(
          eventLog.payload.fullTextReviewVote.vote.toLowerCase()
        );
  },
  details: (eventLog, shouldBeBlind) => {
    if (isVoteBlind(eventLog, shouldBeBlind)) return null;
    const { payload } = eventLog;
    return {
      criterion:
        payload.fullTextReviewVote.reasonReviewCriteria?.description,
      notes: payload.fullTextReviewVote.notes,
    };
  },
};

const FULL_TEXT_REVIEW_OUTCOME_STYLE_PROPS = {
  target: ({ event }) =>
    event === ReferenceEvent.FULL_TEXT_REVIEW_OUTCOME_CREATE
      ? 'Reviewed'
      : 'Full Text Review Outcome',
  verbs: ({ event }) =>
    event === ReferenceEvent.FULL_TEXT_REVIEW_OUTCOME_CREATE
      ? 'as'
      : 'changed to',
  icon: ({ payload }, shouldBeBlind) =>
    shouldBeBlind
      ? { name: 'help', color: 'grey darken-2' }
      : getIcon(payload.fullTextReviewOutcome.outcome),
  outcome: ({ payload }, shouldBeBlind) => {
    return shouldBeBlind
      ? '-'
      : startCase(
          payload.fullTextReviewOutcome.outcome.toLowerCase()
        );
  },
  color: ({ payload }, shouldBeBlind) => {
    if (shouldBeBlind) return null;
    return payload.fullTextReviewOutcome.outcome ===
      InclusionAssessment.INCLUDE
      ? 'success'
      : 'error';
  },
  details: ({ payload }, shouldBeBlind) => {
    if (shouldBeBlind) return null;
    return {
      criterion:
        payload.fullTextReviewOutcome.reasonReviewCriteria
          ?.description,
      notes: null,
    };
  },
};

const EVENT_TO_STYLE_PROPS = {
  [ReferenceEvent.FULL_TEXT_REVIEW_VOTE_CREATE]: FULL_TEXT_REVIEW_VOTE_STYLE_PROPS,
  [ReferenceEvent.FULL_TEXT_REVIEW_VOTE_UPDATE]: FULL_TEXT_REVIEW_VOTE_STYLE_PROPS,
  [ReferenceEvent.FULL_TEXT_REVIEW_VOTE_CONFLICT]: {
    icon: { name: 'error', color: 'error' },
    target: 'Full Text Review Vote Conflict Detected',
    verbs: null,
    outcome: null,
    color: 'error',
  },
  [ReferenceEvent.FULL_TEXT_REVIEW_VOTE_REVOKE]: {
    icon: { name: 'undo', color: 'error' },
    target: 'Full Text Review Vote',
    verbs: 'revoked',
    outcome: null,
    color: 'error',
  },
  [ReferenceEvent.FULL_TEXT_REVIEW_OUTCOME_CREATE]: FULL_TEXT_REVIEW_OUTCOME_STYLE_PROPS,
  [ReferenceEvent.FULL_TEXT_REVIEW_OUTCOME_UPDATE]: FULL_TEXT_REVIEW_OUTCOME_STYLE_PROPS,
  [ReferenceEvent.FULL_TEXT_REVIEW_OUTCOME_REVOKE]: {
    icon: { name: 'undo', color: 'error' },
    target: 'Full Text Review Outcome',
    verbs: 'revoked',
    outcome: null,
    color: 'error',
  },
};

export default {
  name: 'FullTextReviewEventLogItem',
  components: { BaseEventLogItem },
  props: {
    referenceEventLog: {
      type: ReferenceEventLog,
      required: true,
    },
    reference: {
      type: Reference,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    bottomNode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    icon() {
      return this.styleProps.icon.name;
    },
    iconColor() {
      return this.styleProps.icon.color;
    },
    target() {
      return this.styleProps.target;
    },
    verbs() {
      return this.styleProps.verbs;
    },
    outcome() {
      return this.styleProps.outcome;
    },
    color() {
      return this.styleProps.color;
    },
    shouldBeBlind() {
      return (
        !this.reference.isReviewed() &&
        this.referenceEventLog.eventBy?.id !==
          this.currentResearcher.id
      );
    },
    details() {
      const details = this.styleProps.details;
      if (!details || (!details.criterion && !details.notes)) {
        return null;
      }
      return details;
    },
    styleProps() {
      return Object.entries(
        EVENT_TO_STYLE_PROPS[this.referenceEventLog.event]
      ).reduce((acc, [key, value]) => {
        acc[key] =
          typeof value === 'function'
            ? value(this.referenceEventLog, this.shouldBeBlind)
            : value;
        return acc;
      }, {});
    },
  },
};
</script>

<style scoped>
.text-overflow-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
